import { RoninSaleOrder } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  CONTRACT,
  ContractEventParams,
  RONIN_GAS_LIMIT,
  useContract
} from '@apeiron/contract'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import {
  CustomError,
  ERROR_CODE,
  useAccountContext,
  useChainSwitcher,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { useMktErrorHandling } from '@apeiron/wallet'
import {
  EXCHANGE_FEE_MULTIPLIER,
  OrderKind,
  TokenStandard
} from '@src/constants/marketplaceOrder'
import useAllowCurrency from '@src/hooks/contract/useAllowCurrency'
import {
  MarketplaceBuyParams,
  MarketplaceCancelSellParams,
  MarketplaceSellParams
} from '@src/hooks/contract/useMarketplace'
import useCreateRoninMarketPlaceOrder from '@src/hooks/graphql/useCreateRoninMarketplaceOrder'
import { addDays, addMonths } from 'date-fns'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Address,
  encodeAbiParameters,
  encodeFunctionData,
  isAddress,
  parseAbiParameter
} from 'viem'

enum InterfaceName {
  ORDER_EXCHANGE = 'ORDER_EXCHANGE',
  ERC1155_EXCHANGE = 'ERC1155_EXCHANGE'
}

const verifyRetryError = new CustomError(
  'verify exceed retry limit',
  ERROR_CODE.CONTRACT.WAIT_SEND_OPERATION_RESULT_TIMEOUT
)

const approvalError = new CustomError(
  'user reject to approve token for operation',
  ERROR_CODE.CONTRACT.APPROVAL_FAIL
)

const SETTLE_INFO_TUPLE_STRUCT = {
  components: [
    {
      internalType: 'bytes',
      name: 'orderData',
      type: 'bytes'
    },
    {
      internalType: 'bytes',
      name: 'signature',
      type: 'bytes'
    },
    {
      internalType: 'address',
      name: 'referralAddr',
      type: 'address'
    },
    {
      internalType: 'uint256',
      name: 'expectedState',
      type: 'uint256'
    },
    {
      internalType: 'address',
      name: 'recipient',
      type: 'address'
    },
    {
      internalType: 'address',
      name: 'refunder',
      type: 'address'
    }
  ],
  internalType: 'struct SettleParameter',
  name: 'settleInfo',
  type: 'tuple'
}

export const SETTLE_ORDER_1155_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    type: 'uint256',
    name: 'settleQuantity'
  },
  {
    internalType: 'uint256',
    name: 'settlePrice',
    type: 'uint256'
  }
]

export const SETTLE_ORDER_721_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    name: 'settlePrice',
    type: 'uint256'
  }
]

export const SWAP_RON_SETTLE_ORDER_1155_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    name: 'settleQuantity',
    type: 'uint256'
  },
  {
    internalType: 'uint256',
    name: 'deadline',
    type: 'uint256'
  },
  {
    internalType: 'address[]',
    name: 'path',
    type: 'address[]'
  }
]

export const SWAP_RON_SETTLE_ORDER_721_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    name: 'deadline',
    type: 'uint256'
  },
  {
    internalType: 'address[]',
    name: 'path',
    type: 'address[]'
  }
]

export const SWAP_TOKENS_SETTLE_ORDER_1155_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    name: 'settleQuantity',
    type: 'uint256'
  },
  {
    internalType: 'uint256',
    name: 'settlePrice',
    type: 'uint256'
  },
  {
    internalType: 'uint256',
    name: 'deadline',
    type: 'uint256'
  },
  {
    internalType: 'address[]',
    name: 'path',
    type: 'address[]'
  }
]

export const SWAP_TOKENS_SETTLE_ORDER_721_FUNCTION_INPUTS = [
  SETTLE_INFO_TUPLE_STRUCT,
  {
    internalType: 'uint256',
    name: 'settlePrice',
    type: 'uint256'
  },
  {
    internalType: 'uint256',
    name: 'deadline',
    type: 'uint256'
  },
  {
    internalType: 'address[]',
    name: 'path',
    type: 'address[]'
  }
]

export const ORDER_DATA_ABI_1155 = parseAbiParameter(
  '(address maker, uint8 kind, (uint8 erc, address addr, uint256 id, uint256 quantity) asset, uint256 expiredAt,address paymentToken,uint256 startedAt,uint256 basePrice,uint256 endedAt,uint256 endedUnitPrice, uint256 expectedState, uint256 nonce)'
)

export const ORDER_DATA_ABI_721 = parseAbiParameter(
  '(address maker, uint8 kind, (uint8 erc,address addr,uint256 id,uint256 quantity)[] assets, uint256 expiredAt, address paymentToken, uint256 startedAt, uint256 basePrice, uint256 endedAt, uint256 endedPrice, uint256 expectedState, uint256 nonce, uint256 marketFeePercentage)'
)

const useRoninMarketplace = (): Hook => {
  const { account } = useAccountContext()

  const { getCurrencyAddress, convertCurrencyPrice, getCurrency } =
    useCurrencyContext()

  const { toWei, sign712TypedData } = useWeb3()

  const { setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  // const { call: getMakerNonce } = useContract(CONTRACT.MARKETPLACE.MAKER_NONCE)

  const { sendWithOption: interactWith } = useContract(
    CONTRACT.MARKETPLACE.GATEWAY_INTERACT_WITH
  )

  const { t } = useTranslation()

  const { call: getState } = useContract(CONTRACT.MARKETPLACE.GET_STATE)

  const { mutate: createRoninOrder } = useCreateRoninMarketPlaceOrder()

  const { config } = useFrontendConfigContext()

  const { getContractAddress } = useChainSwitcher()

  const { requestCurrency } = useAllowCurrency()

  const handleError = useCallback(
    async (exec: () => Promise<void>) => {
      try {
        await exec()

        return true
      } catch (e) {
        showErrorPopup(e)

        return false
      }
    },
    [showErrorPopup]
  )

  const createListing = useCallback(
    async (params: MarketplaceSellParams): Promise<boolean> => {
      const { approve, verify } = params

      setInfo({
        title: t('dialog.sell.creating_order'),
        message: t('common.popup.transaction.be_patient')
      })

      const from = account.walletAddress || ''

      const now = new Date()

      const expiredAt = addMonths(now, 6) // Default expired after 6 months

      const endedAt = params.contractParams.duration
        ? addDays(now, params.contractParams.duration)
        : new Date(0)

      const gatewayContract = CONTRACT.MARKETPLACE.GATEWAY_INTERACT_WITH

      const contractAddress = getContractAddress(gatewayContract.address)

      const isSuccess: boolean = await handleError(async () => {
        const approved = await approve(contractAddress)

        if (!approved) {
          throw approvalError
        }

        // TODO - now only 0 is supported when creating order
        // const nonce = await getMakerNonce(from, !params.contractParams.erc1155)
        const nonce = 0

        const erc = params.contractParams.erc1155
          ? TokenStandard.ERC1155
          : TokenStandard.ERC721

        const expectedState = await getState([
          [
            erc,
            params.contractParams.nftAddress,
            params.contractParams.nftId,
            1
          ]
        ])

        if (params.contractParams.erc1155) {
          const order = {
            maker: from,
            kind: OrderKind.Sale,
            asset: {
              erc,
              addr: params.contractParams.nftAddress,
              id: params.contractParams.nftId,
              quantity: params.contractParams.erc1155?.quantity.toString()
            },
            expiredAt: Math.round(expiredAt.getTime() / 1000),
            paymentToken: params.contractParams.tokenAddress,
            startedAt: Math.round(now.getTime() / 1000),
            unitPrice: params.contractParams.startPrice,
            endedAt: Math.round(endedAt.getTime() / 1000),
            endedUnitPrice:
              endedAt.getTime() > 0 ? params.contractParams.endPrice : '0',
            expectedState,
            nonce
          }

          const data = {
            ...(config?.roninMarketplace.createOrderTypedDataErc1155 || {}),
            message: order
          }

          const signature = await sign712TypedData(data)

          await createRoninOrder({
            maker: order.maker,
            asset: {
              erc: order.asset.erc,
              addr: order.asset.addr,
              id: order.asset.id,
              quantity: order.asset.quantity
            },
            paymentToken: order.paymentToken,
            basePrice: order.unitPrice,
            endedPrice: order.endedUnitPrice,
            expectedState: order.expectedState,
            nonce: order.nonce,
            marketFeePercentage: 0,
            expiredAt: new Date(order.expiredAt * 1000).toISOString(),
            startedAt: new Date(order.startedAt * 1000).toISOString(),
            endedAt: new Date(order.endedAt * 1000).toISOString(),
            signature
          })
        } else {
          const order = {
            maker: from,
            kind: OrderKind.Sale,
            assets: [
              {
                erc,
                addr: params.contractParams.nftAddress,
                id: params.contractParams.nftId,
                quantity: '0'
              }
            ],
            expiredAt: Math.round(expiredAt.getTime() / 1000),
            paymentToken: params.contractParams.tokenAddress,
            startedAt: Math.round(now.getTime() / 1000),
            basePrice: params.contractParams.startPrice,
            endedAt: Math.round(endedAt.getTime() / 1000),
            endedPrice:
              endedAt.getTime() > 0 ? params.contractParams.endPrice : '0',
            expectedState,
            nonce,
            marketFeePercentage: 0
          }

          const data = {
            ...(config?.roninMarketplace.createOrderTypedDataErc721 || {}),
            message: order
          }

          const signature = await sign712TypedData(data)

          await createRoninOrder({
            maker: order.maker,
            asset: {
              erc: order.assets[0].erc,
              addr: order.assets[0].addr,
              id: order.assets[0].id,
              quantity: order.assets[0].quantity
            },
            paymentToken: order.paymentToken,
            basePrice: order.basePrice,
            endedPrice: order.endedPrice,
            expectedState: order.expectedState,
            nonce: order.nonce,
            marketFeePercentage: order.marketFeePercentage,
            expiredAt: new Date(order.expiredAt * 1000).toISOString(),
            startedAt: new Date(order.startedAt * 1000).toISOString(),
            endedAt: new Date(order.endedAt * 1000).toISOString(),
            signature
          })
        }

        const success = await verify(params.contractParams.nftId)

        if (!success) {
          throw verifyRetryError
        }
      })

      // wait for 3 seconds for listing info to be indexed
      await new Promise(resolve => setTimeout(resolve, 3000))

      setInfo(null)

      return isSuccess
    },
    [
      setInfo,
      t,
      account.walletAddress,
      getContractAddress,
      handleError,
      getState,
      config?.roninMarketplace.createOrderTypedDataErc1155,
      config?.roninMarketplace.createOrderTypedDataErc721,
      createRoninOrder,
      sign712TypedData
    ]
  )

  const encode1155OrderData = useCallback(
    async (ownerAddress: Address, roninSaleOrder: RoninSaleOrder) => {
      const orderData = {
        maker: ownerAddress,
        kind: OrderKind.Sale,
        asset: {
          erc: roninSaleOrder.asset.erc,
          addr: roninSaleOrder.asset.addr as Address,
          id: BigInt(roninSaleOrder.asset.id),
          quantity: BigInt(roninSaleOrder?.asset.quantity)
        },
        expiredAt: BigInt(
          Math.round(new Date(roninSaleOrder.expiredAt).getTime() / 1000)
        ),
        paymentToken: roninSaleOrder.paymentToken as Address,
        startedAt: BigInt(
          Math.round(new Date(roninSaleOrder.startedAt).getTime() / 1000)
        ),
        basePrice: BigInt(roninSaleOrder.basePrice),
        endedAt: BigInt(
          Math.round(new Date(roninSaleOrder.endedAt).getTime() / 1000)
        ),
        endedUnitPrice: BigInt(roninSaleOrder.endedPrice),
        expectedState: BigInt(roninSaleOrder.expectedState),
        nonce: BigInt(roninSaleOrder.nonce)
      }

      const encodedOrderData = encodeAbiParameters(
        [ORDER_DATA_ABI_1155],
        [orderData]
      )

      return encodedOrderData
    },
    []
  )

  const encode721OrderData = useCallback(
    async (ownerAddress: Address, roninSaleOrder: RoninSaleOrder) => {
      const orderData = {
        maker: ownerAddress,
        kind: OrderKind.Sale,
        assets: [
          {
            erc: roninSaleOrder.asset.erc,
            addr: roninSaleOrder.asset.addr as Address,
            id: BigInt(roninSaleOrder.asset.id),
            quantity: BigInt(roninSaleOrder?.asset.quantity)
          }
        ],
        expiredAt: BigInt(
          Math.round(new Date(roninSaleOrder.expiredAt).getTime() / 1000)
        ),
        paymentToken: roninSaleOrder.paymentToken as Address,
        startedAt: BigInt(
          Math.round(new Date(roninSaleOrder.startedAt).getTime() / 1000)
        ),
        basePrice: BigInt(roninSaleOrder.basePrice),
        endedAt: BigInt(
          Math.round(new Date(roninSaleOrder.endedAt).getTime() / 1000)
        ),
        endedPrice: BigInt(roninSaleOrder.endedPrice),
        expectedState: BigInt(roninSaleOrder.expectedState),
        nonce: BigInt(roninSaleOrder.nonce),
        marketFeePercentage: BigInt(roninSaleOrder.marketFeePercentage)
      }

      const encodedOrderData = encodeAbiParameters(
        [ORDER_DATA_ABI_721],
        [orderData]
      )

      return encodedOrderData
    },
    []
  )

  const cancelListing = useCallback(
    async (params: MarketplaceCancelSellParams): Promise<boolean> => {
      console.info(`cancelListing ${params}`)

      const { verify } = params

      if (
        !params.contractParams.roninSaleOrder ||
        !isAddress(account.walletAddress)
      ) {
        return false
      }

      setInfo({
        title: t('dialog.cancel_sell.cancelling'),
        message: t('common.popup.transaction.be_patient')
      })

      const encodedOrderData = params.contractParams.erc1155
        ? await encode1155OrderData(
            account.walletAddress,
            params.contractParams.roninSaleOrder
          )
        : await encode721OrderData(
            account.walletAddress,
            params.contractParams.roninSaleOrder
          )

      const encodedFunctionData = encodeFunctionData({
        abi: [
          {
            name: 'cancelOrder',
            type: 'function',
            inputs: [
              {
                type: 'bytes',
                name: 'orderData'
              }
            ]
          }
        ],
        args: [encodedOrderData]
      })

      const isSuccess = await handleError(async () => {
        await interactWith(
          { verifyEvent: ContractEventParams.OrderCancelled },
          params.contractParams.erc1155
            ? InterfaceName.ERC1155_EXCHANGE
            : InterfaceName.ORDER_EXCHANGE,
          encodedFunctionData
        )

        const success = verify
          ? await verify(params.contractParams.nftId)
          : true

        if (!success) {
          throw verifyRetryError
        }
      })

      return isSuccess
    },
    [
      account.walletAddress,
      encode1155OrderData,
      encode721OrderData,
      handleError,
      interactWith,
      setInfo,
      t
    ]
  )

  const encodeFunctionCallByInterface = useCallback(
    async (
      ercInterface: InterfaceName,
      settleInfoTuple: any,
      settleQuantity: number | undefined | null,
      settlePriceString: string,
      settleOrderTokenType: CurrencyTokenType,
      paymentTokenAddress: string
    ) => {
      const ronAddress = getCurrencyAddress(CurrencyTokenType.WRon)

      const deadline = Math.round(new Date().getTime() / 1000) + 60 * 20

      const settleOrderTokenContractAddress =
        settleOrderTokenType === CurrencyTokenType.Ron
          ? ronAddress
          : getCurrencyAddress(settleOrderTokenType)

      if (settleOrderTokenContractAddress === paymentTokenAddress) {
        return encodeFunctionData({
          abi: [
            {
              name: 'settleOrder',
              type: 'function',
              inputs:
                ercInterface === InterfaceName.ORDER_EXCHANGE
                  ? SETTLE_ORDER_721_FUNCTION_INPUTS
                  : SETTLE_ORDER_1155_FUNCTION_INPUTS
            }
          ],
          args:
            ercInterface === InterfaceName.ORDER_EXCHANGE
              ? [settleInfoTuple as any, settlePriceString]
              : [settleInfoTuple as any, settleQuantity, settlePriceString]
        })
      } else if (settleOrderTokenType === CurrencyTokenType.Ron) {
        return encodeFunctionData({
          abi: [
            {
              name: 'swapRONAndSettleOrder',
              type: 'function',
              inputs:
                ercInterface === InterfaceName.ORDER_EXCHANGE
                  ? SWAP_RON_SETTLE_ORDER_721_FUNCTION_INPUTS
                  : SWAP_RON_SETTLE_ORDER_1155_FUNCTION_INPUTS
            }
          ],
          args:
            ercInterface === InterfaceName.ORDER_EXCHANGE
              ? [
                  settleInfoTuple as any,
                  deadline,
                  [settleOrderTokenContractAddress, paymentTokenAddress]
                ]
              : [
                  settleInfoTuple as any,
                  settleQuantity,
                  deadline,
                  [settleOrderTokenContractAddress, paymentTokenAddress]
                ]
        })
      } else {
        const path =
          settleOrderTokenType === CurrencyTokenType.Aprs
            ? [settleOrderTokenContractAddress, ronAddress, paymentTokenAddress]
            : [settleOrderTokenContractAddress, paymentTokenAddress]

        const encodedFunctionParams =
          ercInterface === InterfaceName.ORDER_EXCHANGE
            ? [settleInfoTuple as any, settlePriceString, deadline, path]
            : [
                settleInfoTuple as any,
                settleQuantity,
                settlePriceString,
                deadline,
                path
              ]

        return encodeFunctionData({
          abi: [
            {
              name: 'swapTokensAndSettleOrder',
              type: 'function',
              inputs:
                ercInterface === InterfaceName.ORDER_EXCHANGE
                  ? SWAP_TOKENS_SETTLE_ORDER_721_FUNCTION_INPUTS
                  : SWAP_TOKENS_SETTLE_ORDER_1155_FUNCTION_INPUTS
            }
          ],
          args: encodedFunctionParams
        })
      }
    },
    [getCurrencyAddress]
  )

  const constructSettle721OrderFunctionData = useCallback(
    async (params: MarketplaceBuyParams, settlePriceString: string) => {
      if (!params.contractParams.roninSaleOrder) {
        return false
      }

      if (!isAddress(params.contractParams.ownerAddress)) {
        return false
      }

      const encodedOrderData = await encode721OrderData(
        params.contractParams.ownerAddress,
        params.contractParams.roninSaleOrder
      )

      const settleInfoTuple = [
        encodedOrderData,
        params.contractParams.roninSaleOrder.signature,
        '0x0000000000000000000000000000000000000000',
        params.contractParams.roninSaleOrder.expectedState,
        account.walletAddress,
        account.walletAddress
      ]

      return encodeFunctionCallByInterface(
        InterfaceName.ORDER_EXCHANGE,
        settleInfoTuple,
        null,
        settlePriceString,
        params.contractParams.settleOrderTokenType,
        params.contractParams.roninSaleOrder.paymentToken
      )
    },
    [account.walletAddress, encode721OrderData, encodeFunctionCallByInterface]
  )

  const constructSettle1155OrderFunctionData = useCallback(
    async (params: MarketplaceBuyParams, settlePriceString: string) => {
      if (!params.contractParams.roninSaleOrder) {
        return false
      }

      if (!isAddress(params.contractParams.ownerAddress)) {
        return false
      }

      const encodedOrderData = await encode1155OrderData(
        params.contractParams.ownerAddress,
        params.contractParams.roninSaleOrder
      )

      const settleInfoTuple = [
        encodedOrderData,
        params.contractParams.roninSaleOrder.signature,
        '0x0000000000000000000000000000000000000000',
        params.contractParams.roninSaleOrder.expectedState,
        account.walletAddress,
        account.walletAddress
      ]

      const encodedFunctionData = encodeFunctionCallByInterface(
        InterfaceName.ERC1155_EXCHANGE,
        settleInfoTuple,
        params.contractParams.erc1155?.quantity,
        settlePriceString,
        params.contractParams.settleOrderTokenType,
        params.contractParams.roninSaleOrder.paymentToken
      )

      return encodedFunctionData
    },
    [account.walletAddress, encode1155OrderData, encodeFunctionCallByInterface]
  )

  const requestAllowance = useCallback(
    async (
      ownerAddress: string,
      contractAddress: Address,
      settlePrice: bigint,
      settleOrderTokenType: CurrencyTokenType = CurrencyTokenType.Weth
    ): Promise<boolean> => {
      if (!isAddress(ownerAddress)) {
        return false
      }

      switch (settleOrderTokenType) {
        case CurrencyTokenType.Weth:
        case CurrencyTokenType.Usdc:
        case CurrencyTokenType.Anima:
        case CurrencyTokenType.Aprs: {
          return requestCurrency(
            ownerAddress,
            contractAddress,
            settleOrderTokenType,
            settlePrice
          )
        }

        case CurrencyTokenType.Ron:
          return true
        default:
          return false
      }
    },
    [requestCurrency]
  )

  const buy = useCallback(
    async (params: MarketplaceBuyParams): Promise<boolean> => {
      const { onApproved, onBought, onFailed } = params

      console.info('buy ', params)
      if (!params.contractParams.roninSaleOrder || !account.walletAddress) {
        return false
      }

      const gatewayContract = CONTRACT.MARKETPLACE.GATEWAY_INTERACT_WITH

      const contractAddress = getContractAddress(gatewayContract.address)

      const settleCurrency = getCurrency(
        params.contractParams.settleOrderTokenType
      )

      const settleOrderTokenType = params.contractParams.settleOrderTokenType

      const convertedPrice =
        convertCurrencyPrice(
          params.contractParams.price,
          CurrencyTokenType.Weth,
          params.contractParams.settleOrderTokenType
        ) *
        (params.contractParams.erc1155?.quantity || 1) *
        (settleOrderTokenType === CurrencyTokenType.Weth
          ? 1
          : EXCHANGE_FEE_MULTIPLIER) // SLIPPAGE TOLERENCE + LIQUIDITY PROVIDER FEE

      const settlePriceForContract = BigInt(
        toWei(convertedPrice, settleCurrency?.decimals || 0)
      )

      const approved = await requestAllowance(
        account.walletAddress,
        contractAddress,
        settlePriceForContract,
        params.contractParams.settleOrderTokenType
      )

      if (!approved) {
        throw approvalError
      }

      onApproved()

      const encodedFunctionData = params.contractParams.erc1155
        ? await constructSettle1155OrderFunctionData(
            params,
            settlePriceForContract.toString()
          )
        : await constructSettle721OrderFunctionData(
            params,
            settlePriceForContract.toString()
          )

      const isSuccess = await handleError(async () => {
        await interactWith(
          {
            verifyEvent: ContractEventParams.OrderMatched,
            ronin:
              params.contractParams.settleOrderTokenType ===
              CurrencyTokenType.Ron
                ? {
                    gasLimit: RONIN_GAS_LIMIT.BUY_ORDER,
                    value: settlePriceForContract.toString()
                  }
                : undefined
          },
          params.contractParams.erc1155
            ? InterfaceName.ERC1155_EXCHANGE
            : InterfaceName.ORDER_EXCHANGE,
          encodedFunctionData
        )

        const success = await params.verify(params.contractParams.nftId)

        if (!success) {
          throw verifyRetryError
        }

        onBought()
      })

      if (!isSuccess) {
        onFailed()
      }

      return isSuccess
    },
    [
      account.walletAddress,
      constructSettle1155OrderFunctionData,
      constructSettle721OrderFunctionData,
      convertCurrencyPrice,
      getContractAddress,
      getCurrency,
      handleError,
      interactWith,
      requestAllowance,
      toWei
    ]
  )

  return {
    buy,
    cancelListing,
    createListing
  }
}

type Hook = {
  buy: (params: MarketplaceBuyParams) => Promise<boolean>
  cancelListing: (params: MarketplaceCancelSellParams) => Promise<boolean>
  createListing: (params: MarketplaceSellParams) => Promise<boolean>
}

export default useRoninMarketplace
